import {
  AdditiveAmountsPicker,
  AmountTextInput,
  AvailableCreditDescription,
} from "@components/TextInput/AmountTextInput";
import { useMaxMatchingAvailable } from "@components/donate/DonateV3/PaymentProcess/useMaxMatchingAvailable";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Big } from "big.js";
import React, { useEffect } from "react";
import { FieldError } from "react-hook-form";

import { CurrencyValue } from "@every.org/common/src/codecs/currency";
import {
  FundraiserResponse,
  NonEmptyMatchingCampaignResponse,
  NonprofitResponse,
  PaymentSourceResponse as PaymentSource,
} from "@every.org/common/src/codecs/entities";
import {
  DonationFlowPaymentOption,
  DonationFrequency,
  PaymentMethod,
} from "@every.org/common/src/entity/types";
import { CustomEvent } from "@every.org/common/src/helpers/analytics";

import { ContextNonprofit } from "src/context/NonprofitsContext/types";
import { colorCssVars } from "src/theme/color";
import {
  horizontalStackCss,
  spacing,
  verticalStackCss,
} from "src/theme/spacing";
import { FontWeight, TextSize, textSizeCss } from "src/theme/text";
import { trackEvent } from "src/utility/analytics";
import { displayCurrencyValueInUserLocale } from "src/utility/currency";

const StyledLabel = styled.label`
  color: var(${colorCssVars.text.body});
  font-weight: ${FontWeight.BOLD};
  ${textSizeCss[TextSize.s]};
`;

function useDonationAmounts({
  nonprofit,
  fundraiser,
  minValue,
  availableGivingCredit,
  suggestedAmountsFromUrl,
  paymentOption,
}: {
  nonprofit: NonprofitResponse | undefined;
  fundraiser?: FundraiserResponse;
  minValue: CurrencyValue;
  availableGivingCredit?: Big;
  suggestedAmountsFromUrl?: number[];
  paymentOption?: DonationFlowPaymentOption;
}): number[] {
  const isHighAmountPaymentOption =
    paymentOption &&
    (paymentOption === DonationFlowPaymentOption.DAF ||
      paymentOption === DonationFlowPaymentOption.IRA);

  if (suggestedAmountsFromUrl?.length) {
    return suggestedAmountsFromUrl;
  }

  const donationAmounts =
    fundraiser?.metadata?.suggestedAmounts ??
    nonprofit?.metadata?.customDonationAmounts;

  if (donationAmounts) {
    return donationAmounts;
  }

  if (availableGivingCredit && availableGivingCredit.gt(minValue.amount)) {
    const upperBound = availableGivingCredit;
    const lowerBound = minValue.amount.times(4).lt(upperBound)
      ? minValue.amount.times(4)
      : upperBound.div(3).gte(minValue.amount)
      ? upperBound.div(3).round(0)
      : minValue.amount;
    const middlePoint = lowerBound.add(upperBound).div(2).round(0);
    return [lowerBound, middlePoint, upperBound].map((n) => Number(n));
  }

  const defaultDonationAmounts = isHighAmountPaymentOption
    ? [
        minValue.amount.times(20),
        minValue.amount.times(200),
        minValue.amount.times(2000),
      ]
    : [
        minValue.amount.times(4),
        minValue.amount.times(10),
        minValue.amount.times(25),
      ];
  return defaultDonationAmounts.map((n) => Number(n));
}

export const Amount = ({
  shorten,
  availableGivingCredit,
  minDonationValue,
  maxDonationValue,
  amount,
  amountBig,
  onChange,
  fieldError,
  nonprofit,
  fundraiser,
  selectedPaymentSource,
  setAmountErrorMessage,
  amountErrorMessage,
  frequency,
  paymentOption,
  paymentMethod,
  suggestedAmountsFromUrl,
  nonprofitMatchCampaign,
  toNonprofits,
  toNonprofitWeights,
}: {
  // Shorten error messages
  shorten?: boolean;
  availableGivingCredit?: Big;
  minDonationValue: CurrencyValue;
  maxDonationValue: CurrencyValue;
  amount: string;
  amountBig: Big;
  onChange: (amount: string) => void;
  fieldError?: FieldError;
  nonprofit?: ContextNonprofit;
  fundraiser?: FundraiserResponse;
  selectedPaymentSource?: PaymentSource;
  setAmountErrorMessage: (message: string | undefined) => void;
  amountErrorMessage: string | undefined;
  frequency?: DonationFrequency;
  paymentOption: DonationFlowPaymentOption;
  paymentMethod: PaymentMethod;
  suggestedAmountsFromUrl?: number[];
  nonprofitMatchCampaign?: NonEmptyMatchingCampaignResponse;
  toNonprofits?: string[];
  toNonprofitWeights?: number[];
}) => {
  const { matchingAmount, maxMatchingAvailable } = useMaxMatchingAvailable({
    amountBig,
    frequency,
    nonprofitMatchCampaign,
    toNonprofitId: nonprofit?.id,
    toNonprofits,
    toNonprofitWeights,
  });

  const donationAmounts = useDonationAmounts({
    nonprofit,
    fundraiser,
    minValue: minDonationValue,
    suggestedAmountsFromUrl,
    paymentOption,
    availableGivingCredit,
  });

  useEffect(() => {
    setAmountErrorMessage(fieldError ? fieldError.message : undefined);
    fieldError && trackEvent(CustomEvent.AMOUNT_ERROR, {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldError]);

  function setDonationAmount(newAmount: string) {
    onChange(newAmount);
  }

  function addDonationAmount(newAmount: number) {
    const increasedAmount = ((parseInt(amount) || 0) + newAmount).toString();
    onChange(increasedAmount);
  }

  const AmountPickerDescriptionElement = () => {
    return (
      <React.Fragment>
        <AdditiveAmountsPicker
          amounts={donationAmounts}
          addAmount={addDonationAmount}
          setAmountErrorMessage={setAmountErrorMessage}
          amountErrorMessage={amountErrorMessage}
        />
        {availableGivingCredit && (
          <AvailableCreditDescription
            availableGivingCredit={availableGivingCredit}
            setAmount={setDonationAmount}
            paymentMethod={paymentMethod}
          />
        )}
        {matchingAmount &&
          nonprofitMatchCampaign &&
          nonprofitMatchCampaign.availableForMatching > 0 && (
            <div
              css={[
                horizontalStackCss.xxs,
                css`
                  align-items: center;
                  margin-top: ${spacing.s};
                `,
              ]}
            >
              <h4
                css={css`
                  color: var(${colorCssVars.text.body});
                `}
              >
                <span
                  css={css`
                    color: var(${colorCssVars.accent.large});
                  `}
                >
                  +
                </span>{" "}
                {displayCurrencyValueInUserLocale({
                  currencyValue: {
                    currency: nonprofitMatchCampaign.currency,
                    amount: matchingAmount,
                  },
                })}
                {" / "}
                {displayCurrencyValueInUserLocale({
                  currencyValue: {
                    currency: nonprofitMatchCampaign.currency,
                    amount: maxMatchingAvailable,
                  },
                })}
                {" matched"}
              </h4>
            </div>
          )}
      </React.Fragment>
    );
  };

  return (
    <fieldset css={verticalStackCss.s}>
      <StyledLabel htmlFor="donateAmountInput">Donation amount</StyledLabel>
      <AmountTextInput
        data-tname="donateAmountTextInputV2"
        id="donateAmountInput"
        name="donateAmountInput"
        shorten={!!shorten}
        setAmount={setDonationAmount}
        minValue={minDonationValue}
        maxValue={maxDonationValue}
        amount={amount}
        amountErrorMessage={fieldError?.message || amountErrorMessage}
        setAmountErrorMessage={setAmountErrorMessage}
        descriptionElement={<AmountPickerDescriptionElement />}
        availableGivingCredit={availableGivingCredit}
        paymentMethod={paymentMethod}
      />
    </fieldset>
  );
};
